import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';

// Persistent anonymous session id (shared with merchant-kit tracker)
function getSessionId() {
  try {
    let sid = localStorage.getItem('mk_session');
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('mk_session', sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

/**
 * Auto-rotating hero promo carousel. Each slide can render a full-slide video and/or
 * image background beneath the text overlay.
 *
 * Slide fields: { id, title, subtitle, cta_label, cta_link,
 *   gradient_from, gradient_to, accent, sponsored_by,
 *   image_url, video_url, active }
 *
 * Behaviour:
 *   - If `video_url` is set, a muted looping `<video>` fills the slide (uses image_url as poster).
 *   - Else if `image_url` is set, the image fills the slide.
 *   - Else the gradient fallback is used.
 *   - A dark scrim overlays media-backed slides so text stays legible.
 *   - Auto-rotate pauses on hover.
 */
export default function PromoCarousel({ promos = [], onWhatsApp }) {
  const slides = promos.filter(p => p.active !== false);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const next = useCallback(() => setIdx(i => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (slides.length < 2 || paused) return undefined;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, slides.length, paused]);

  // Force <video> to reload when slide changes so the new src plays
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      const p = videoRef.current.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [idx]);

  // Fire an impression beacon whenever a promo is shown (dedupe client-side per session)
  const firedRef = useRef(new Set());
  useEffect(() => {
    if (!slides.length) return;
    const cur = slides[idx];
    if (!cur?.id || firedRef.current.has(cur.id)) return;
    firedRef.current.add(cur.id);
    const sid = getSessionId();
    axios.post(`${API}/promos/impression`, { promo_id: cur.id, session_id: sid })
      .catch(() => {}); // fire-and-forget; server also dedupes per-hour
  }, [idx, slides]);

  if (slides.length === 0) return null;
  const slide = slides[idx];

  const hasVideo = !!slide.video_url;
  const hasImage = !!slide.image_url;
  const hasMedia = hasVideo || hasImage;
  const gradientBg = `linear-gradient(135deg, ${slide.gradient_from || '#0A1F44'}, ${slide.gradient_to || '#1E40AF'})`;

  const handleCta = () => {
    // Fire click beacon (fire-and-forget)
    if (slide?.id) {
      const sid = getSessionId();
      axios.post(`${API}/promos/click`, { promo_id: slide.id, session_id: sid }).catch(() => {});
    }
    if (slide.cta_link === 'whatsapp') {
      onWhatsApp?.();
      return;
    }
    if (slide.cta_link?.startsWith('http')) {
      window.open(slide.cta_link, '_blank', 'noopener,noreferrer');
      return;
    }
    if (slide.cta_link) navigate(slide.cta_link);
  };

  return (
    <div
      className="promo-carousel"
      data-testid="promo-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className={`promo-slide ${hasMedia ? 'has-media' : ''}`}
        style={{ background: hasMedia ? '#0A1F44' : gradientBg }}
        data-testid={`promo-slide-${slide.id}`}
      >
        {/* Media background layer */}
        {hasVideo && (
          <video
            ref={videoRef}
            className="promo-media promo-media-video"
            src={slide.video_url}
            poster={slide.image_url || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid={`promo-video-${slide.id}`}
          />
        )}
        {!hasVideo && hasImage && (
          <img
            className="promo-media promo-media-image"
            src={slide.image_url}
            alt={slide.title || 'Promo'}
            data-testid={`promo-image-${slide.id}`}
          />
        )}
        {hasMedia && <div className="promo-scrim" aria-hidden="true" />}

        {slide.slot_type === 'premium' && (
          <div className="promo-premium-badge" data-testid={`promo-premium-${slide.id}`}>
            <span aria-hidden="true">★</span> Featured
          </div>
        )}

        {slide.sponsored_by && (
          <div className="promo-sponsored" data-testid={`promo-sponsored-${slide.id}`}>
            <span className="promo-sponsored-label">Sponsored</span>
            <span className="promo-sponsored-name">{slide.sponsored_by}</span>
          </div>
        )}

        <div className="promo-slide-inner">
          <div className="promo-text">
            <h2 style={{ color: slide.accent || '#fff' }} data-testid="promo-title">{slide.title}</h2>
            {slide.subtitle && <p data-testid="promo-subtitle">{slide.subtitle}</p>}
            {slide.cta_label && (
              <button onClick={handleCta} className="promo-cta" data-testid="promo-cta-btn"
                style={{ background: slide.accent || '#C8FF00' }}>
                {slide.cta_label} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        {slides.length > 1 && (
          <>
            <button className="promo-arrow promo-arrow-prev" onClick={prev} aria-label="Previous" data-testid="promo-prev">
              <ChevronLeft size={18} />
            </button>
            <button className="promo-arrow promo-arrow-next" onClick={next} aria-label="Next" data-testid="promo-next">
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <div className="promo-dots" data-testid="promo-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`promo-dot ${i === idx ? 'active' : ''}`}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`promo-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
